import React from "react";
import { formatMetric, formatNumber } from "./clusterFormat";

// Search traffic that matches NO tracked cluster.
//
// This is reference data, NOT a scorecard. The cluster strategy is a deliberate
// narrow bet — high-impression terms that never converted were dropped on
// purpose — so most untracked traffic is legacy demand that is expected to
// decay. Presenting it as "coverage you should increase" would argue for
// undoing that decision, so the panel is explicitly framed as informational
// and carries no target or warning colour.

const UntrackedDemandPanel = ({ snapshot }) => {
  if (!snapshot) return null;

  const total = snapshot.site?.impressions ?? 0;
  const untracked = snapshot.unattributedImpressions ?? 0;
  const rows = snapshot.topUnattributed || [];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
      <div className="flex items-baseline justify-between mb-1">
        <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">
          Traffic outside the plan
        </p>
        <p className="text-sm font-semibold text-gray-500">
          {formatNumber(untracked)} / {formatNumber(total)} impr
        </p>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Queries the site still ranks for that no cluster targets — mostly legacy demand from
        before the current plan. Shown for reference, not as a gap to close: these are largely
        terms that were dropped for not converting.
      </p>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          Every query in this window matched a tracked cluster.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4 font-medium">Query</th>
                <th className="py-2 pr-4 font-medium text-right">Impressions</th>
                <th className="py-2 pr-4 font-medium text-right">Clicks</th>
                <th className="py-2 font-medium text-right">Position</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.query} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-2 pr-4 text-gray-800">{row.query}</td>
                  <td className="py-2 pr-4 text-right text-gray-700">
                    {formatNumber(row.impressions)}
                  </td>
                  <td className="py-2 pr-4 text-right text-gray-700">
                    {formatNumber(row.clicks)}
                  </td>
                  {/* Position stays an em dash when unmeasured — never 0. */}
                  <td className="py-2 text-right text-gray-700">{formatMetric(row.position)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UntrackedDemandPanel;
